import { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { Box, Grid2, Stack, Typography, useTheme } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { EGBasicSelect } from "../../../../MainComponents/Select/EGBasicSelect";
import { EGButton } from "../../../../MainComponents/Button/EGButton/EGButton";
import { returnIsValid, useConstants } from "../Main/useConstants";
import { useSelector } from "react-redux";
import { SectionedSelect } from "../../../../MainComponents/Select/SectionedSelect";
import { useReport } from "../../../../../context/report/ReportContext";

// Funcion revisar el formulario y retornar si es valido y cuales son los errores
export const validateForm = (form) => {
  const { kind, name, indicated, status } = form;
  const errors = {};
  if (kind.trim() === "") {
    errors.kind = true;
  }
  if (name.trim() === "") {
    errors.name = true;
  }
  if (indicated === null) {
    errors.indicated = true;
  }
  if (typeof status === "string" && status.trim() === "") {
    errors.status = true;
  }
  if (typeof status === "boolean") {
    errors.status = true;
  }
  return [Object.entries(errors).length === 0 ? true : false, errors];
};

// Agregar un involucrado
export const AddInvolved = ({
  handleSaveInvolveds,
  involvedsInInvestigation,
  setIsAdding,
  isLoading = false,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  // Fuente de los usuarios para "Señalado por" (sesión, denunciante y asignados)
  const { state: reportState } = useReport();
  const {
    enums,
    selects: { typeUserLabels },
    involveds: {
      involvedTypeOpts,
      colaboratorOpts,
      statusOpts,
      genderOpts,
      reinsidenceOpts,
    },
  } = useConstants();

  const [kind, setKind] = useState("");
  const [name, setName] = useState("");
  const [collaborator, setCollaborator] = useState("");
  const [additional, setAdditional] = useState("");
  const [area, setArea] = useState("");
  const [relationship, setRelationship] = useState("");
  const [indicated, setIndicated] = useState(null);

  const [status, setStatus] = useState(statusOpts[0].value);
  const [details, setDetails] = useState("");
  const [gender, setGender] = useState("");
  const [reinsidence, setReinsidence] = useState("");
  const [triedToAdd, setTriedToAdd] = useState(false);
  const [errors, setErrors] = useState({});

  const authState = useSelector((state) => state.auth);

  const indicatedOpts = useMemo(() => {
    const {
      ME,
      COMPLAINANT,
      INVOLVEDS_ACCOMPLICE,
      INVOLVEDS_OFFENDER,
      INVOLVEDS_VICTIM,
      INVOLVEDS_WITNESS,
      SYSTEM,
    } = enums.typeUser;
    const usersWanted = [
      ME,
      COMPLAINANT,
      INVOLVEDS_VICTIM,
      INVOLVEDS_ACCOMPLICE,
      INVOLVEDS_OFFENDER,
      INVOLVEDS_WITNESS,
      SYSTEM,
    ];

    let newIndicatedOpts = [];

    usersWanted.forEach((value) => {
      if (value === ME) {
        newIndicatedOpts.push({
          key: value,
          options: [
            {
              value: authState.email,
              label: authState.username || authState.email || t("type_users.ME"),
            },
          ],
        });
        return;
      }
      if (value === COMPLAINANT) {
        newIndicatedOpts.push({
          key: value,
          options: [
            {
              value: COMPLAINANT,
              label: t("involved.reporter"),
            },
          ],
        });
        return;
      }

      // Lista de cada tipo (víctimas, ofensores, etc.) según su ruta en reportState
      const path = _.get(enums, ["typeUserRoutes", value]);
      const node = _.get(reportState, path);
      const fromArray =
        (node &&
          node.formatted &&
          Array.isArray(node.formatted) &&
          node.formatted) ||
        (node && node.raw && Array.isArray(node.raw) && node.raw) ||
        (Array.isArray(node) && node) ||
        [];

      newIndicatedOpts.push({
        label: typeUserLabels[value],
        key: value,
        options: fromArray,
      });
    });

    return newIndicatedOpts;
  }, [reportState, enums, typeUserLabels, t, authState]);

  // Valfroate every change
  useEffect(() => {
    if (triedToAdd) {
      const [, errors] = validateForm({ kind, name, indicated, status });
      setErrors(errors);
    }
  }, [kind, name, indicated, status]);

  const handleAddInvolved = async () => {
    const isInternal = collaborator === "internal";
    const [isValid, errors] = validateForm({ kind, name, indicated, status });
    setErrors(errors);
    setTriedToAdd(true);
    if (!isValid) {
      enqueueSnackbar({
        variant: "error",
        message: t("missing_required_inputs"),
      });
      return;
    }
    const newInvolveds = _.cloneDeep(involvedsInInvestigation) ?? {};
    if (!Array.isArray(newInvolveds[kind])) newInvolveds[kind] = [];
    const data = {
      kind,
      rol: kind,
      name,
      collaborator,
      additional: isInternal ? additional : "", // # empleado
      area: isInternal ? area : "", // area o departamento
      relationship: isInternal ? relationship : "", // puesto
      indicated,
      gender,
      reinsidence,
      validated: false,
      confirm: status === "confirmed" ? true : false,
      confirm_text: status,
      details,
    };
    newInvolveds[kind].push(data);
    await handleSaveInvolveds(newInvolveds);
  };

  return (
    <Box padding={2}>
      <Box display="flex" alignItems="center" marginBottom={1.5}>
        <Typography
          fontWeight={600}
          fontSize={16}
          color={theme.palette.ethicsBlue.main}
        >
          {t("involved.add_new")}
        </Typography>
        <Box
          flexGrow={1}
          height="1px"
          sx={{
            backgroundColor: theme.palette.ethicsBlue.main,
            marginLeft: 1,
          }}
        />
      </Box>

      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <EGBasicSelect
            label={t("involved.type")}
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            options={involvedTypeOpts}
            required={true}
            isValid={returnIsValid(errors?.kind, triedToAdd)}
            showSuccessValidation={triedToAdd}
            showHelpText={false}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, lg: 6 }}>
          <EGInput
            label={t("involved.full_name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required={true}
            isValid={returnIsValid(errors?.name, triedToAdd)}
            showSuccessValidation={triedToAdd}
            showHelpText={false}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, lg: 6 }}>
          <EGBasicSelect
            label={t("involved.collaborator_type")}
            value={collaborator}
            onChange={(e) => setCollaborator(e.target.value)}
            options={colaboratorOpts}
          />
        </Grid2>

        {collaborator === "internal" && (
          <>
            <Grid2 size={{ xs: 12, lg: 6 }}>
              <EGInput
                label={t("involved.employee_number")}
                value={additional}
                onChange={(e) => setAdditional(e.target.value)}
                type="text"
              />
            </Grid2>

            <Grid2 size={{ xs: 12, lg: 6 }}>
              <EGInput
                label={t("involved.area")}
                value={area}
                onChange={(e) => setArea(e.target.value)}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, lg: 6 }}>
              <EGInput
                label={t("involved.position")}
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
              />
            </Grid2>
          </>
        )}

        <Grid2 size={{ xs: 12, lg: 6 }}>
          <SectionedSelect
            label={t("involved.indicated_by")}
            value={indicated?.id ?? ""}
            onChange={({ value, section, label }) =>
              setIndicated({ id: value, from: section, label })
            }
            sections={indicatedOpts}
            required
            isValid={returnIsValid(errors?.indicated, triedToAdd)}
            showSuccessValidation={true}
            showHelpText={false}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, lg: 6 }}>
          <EGBasicSelect
            label={t("involved.gender")}
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            options={genderOpts}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, lg: 6 }}>
          <EGBasicSelect
            label={t("involved.reinsidence")}
            value={reinsidence}
            onChange={(e) => setReinsidence(e.target.value)}
            options={reinsidenceOpts}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, lg: 6 }}>
          <EGBasicSelect
            label={t("involved.status")}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={statusOpts}
            required={true}
            isValid={returnIsValid(errors?.status, triedToAdd)}
            showSuccessValidation={triedToAdd}
            showHelpText={false}
          />
        </Grid2>

        <Grid2 size={12}>
          <EGInput
            label={t("involved.details")}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            type="textarea"
            multiline
            rows={5}
          />
        </Grid2>
      </Grid2>

      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="flex-end"
        spacing={1}
        marginTop={2}
      >
        <EGButton
          label={t("involved.cancel")}
          color="secondary"
          size="medium"
          iconName="close_icon"
          onClick={() => setIsAdding(false)}
          loading={isLoading}
          disabled={isLoading}
        />
        <EGButton
          label={t("involved.save")}
          color="success"
          labelColor="white"
          size="medium"
          iconName="mark_check_bold"
          iconColor="white"
          iconColorHover="white"
          onClick={handleAddInvolved}
          loading={isLoading}
          disabled={isLoading}
        />
      </Stack>
    </Box>
  );
};
